import { lazy, Suspense } from "react";
import { BookOpen, Bot, Briefcase, Building2, FileText, Gavel, Globe, Landmark, LineChart, Scale, Settings2, Sparkles, Users, type LucideIcon, type LucideProps } from "lucide-react";
import { cn } from "@ncp/ui";

/**
 * Icons of workspaces, assistants, solutions and knowledge collections are stored as lucide names (`Scale`, `Landmark`). The common
 * ones ship statically; anything else loads on demand — lucide's full name list and dynamic loader included (PlatformIconDynamic) — so
 * the console can pick from the full set and no app carries that list in its first paint.
 */
const STATIC: Record<string, LucideIcon> = { Scale, Landmark, BookOpen, Sparkles, Briefcase, Globe, Settings2, Bot, FileText, Gavel, Building2, Users, LineChart };

const Dynamic = lazy(() => import("./PlatformIconDynamic"));

export function PlatformIcon({ name, fallback = Sparkles, ...props }: { name?: string | null; fallback?: LucideIcon } & Omit<LucideProps, "name">) {
  const F = fallback;
  if (!name) return <F {...props} />;
  const Static = STATIC[name];
  if (Static) return <Static {...props} />;
  return (
    <Suspense fallback={<F {...props} />}>
      <Dynamic {...props} name={name} fallback={F} />
    </Suspense>
  );
}

/** Palette names used by the console (`gold`, `sea`, `camel`, `sand`, `green`, `red`, `dark`) → tile classes. */
export function tileClasses(color?: string | null, className?: string): string {
  const tone = {
    gold: "bg-aegold-100 text-aegold-800",
    sea: "bg-seablue-100 text-seablue-800",
    camel: "bg-camel-100 text-camel-800",
    sand: "bg-sand-200 text-aeblack-800",
    green: "bg-success-100 text-success-700",
    red: "bg-danger-100 text-danger-700",
    dark: "bg-aeblack-950 text-white",
  }[color ?? "gold"] ?? "bg-aegold-100 text-aegold-800";
  return cn("grid shrink-0 place-items-center rounded-xl", tone, className);
}

/** Accent classes (text / border) for the same palette, for badges and active states. */
export function accentClasses(color?: string | null): { text: string; border: string; bg: string } {
  return {
    gold: { text: "text-aegold-800", border: "border-aegold-300", bg: "bg-aegold-50" },
    sea: { text: "text-seablue-800", border: "border-seablue-300", bg: "bg-seablue-50" },
    camel: { text: "text-camel-800", border: "border-camel-300", bg: "bg-camel-50" },
    sand: { text: "text-aeblack-800", border: "border-sand-300", bg: "bg-sand-100" },
    green: { text: "text-success-700", border: "border-success-200", bg: "bg-success-50" },
    red: { text: "text-danger-700", border: "border-danger-200", bg: "bg-danger-50" },
    dark: { text: "text-aeblack-950", border: "border-aeblack-800", bg: "bg-sand-100" },
  }[color ?? "gold"] ?? { text: "text-aegold-800", border: "border-aegold-300", bg: "bg-aegold-50" };
}
